import { FIELDS, FILTER_FIELDS, valuesOf } from "./fields";
import { cellText } from "./sheet";
import { parseReach, uniqueCaseless } from "./parse";

// Filters live in the URL (?territories=UK&formats=HbbTV&q=crime), so a
// filtered list can be bookmarked or sent to someone as a link.
//
// Within one filter any selected value matches (UK or Germany); across
// filters all must match (UK and HbbTV).

export function readFilters(params) {
  const lists = {};
  FILTER_FIELDS.forEach((f) => {
    const values = params.getAll(f.key).filter(Boolean);
    if (values.length) lists[f.key] = values;
  });
  return {
    q: params.get("q") || "",
    lists,
    cpmMax: params.get("cpmMax") || "",
    reachMin: params.get("reachMin") || "",
  };
}

export function writeFilters(filters, base) {
  const params = new URLSearchParams(base);
  ["q", "cpmMax", "reachMin", ...FILTER_FIELDS.map((f) => f.key)].forEach((k) =>
    params.delete(k)
  );
  if (filters.q) params.set("q", filters.q);
  Object.entries(filters.lists).forEach(([key, values]) =>
    values.forEach((v) => params.append(key, v))
  );
  ["cpmMax", "reachMin"].forEach((k) => {
    if (filters[k] !== "") params.set(k, filters[k]);
  });
  return params;
}

export function activeFilterCount(filters) {
  return (
    (filters.q ? 1 : 0) +
    Object.values(filters.lists).reduce((n, v) => n + v.length, 0) +
    ["cpmMax", "reachMin"].filter((k) => filters[k] !== "").length
  );
}

const lower = (s) => String(s).toLowerCase();

export function applyFilters(rows, filters) {
  const words = lower(filters.q).split(/\s+/).filter(Boolean);
  const cpmMax = filters.cpmMax === "" ? null : parseFloat(filters.cpmMax);
  const reachMin = filters.reachMin === "" ? null : parseReach(filters.reachMin);
  const lists = Object.entries(filters.lists).map(([key, values]) => [
    key,
    new Set(values.map(lower)),
  ]);

  return rows.filter((row) => {
    for (const [key, wanted] of lists) {
      if (!valuesOf(row, key).some((v) => wanted.has(lower(v)))) return false;
    }
    if (cpmMax != null && !(row.cpm <= cpmMax)) return false;
    if (reachMin != null && !(row.reach >= reachMin)) return false;
    if (words.length) {
      // Every word has to appear somewhere in the row: "uk crime" finds UK
      // crime channels, not every UK channel plus every crime channel.
      const haystack = lower(FIELDS.map((f) => cellText(row, f)).join(" | "));
      if (!words.every((w) => haystack.includes(w))) return false;
    }
    return true;
  });
}

// The choices for one filter, with how many channels have each. Known
// options for the field (HbbTV, CTV, ...) are offered even before any channel
// uses them.
export function filterOptions(rows, field) {
  const counts = new Map();
  rows.forEach((row) =>
    uniqueCaseless(valuesOf(row, field.key)).forEach((v) => {
      const key = lower(v);
      const entry = counts.get(key) || { value: v, count: 0 };
      entry.count += 1;
      counts.set(key, entry);
    })
  );
  (field.options || []).forEach((v) => {
    if (!counts.has(lower(v))) counts.set(lower(v), { value: v, count: 0 });
  });
  return [...counts.values()].sort(
    (a, b) => b.count - a.count || a.value.localeCompare(b.value)
  );
}
